import React, { useState } from "react";
import { autoUpdate, offset, shift } from "@floating-ui/dom";
import { useFloating } from "@floating-ui/react";
import { TrendComparison } from "app/data/terminal-insights/models";

interface TrendTooltipProps {
  vsYesterday: TrendComparison | null;
  vsLastWeek?: TrendComparison | null;
  higherIsBetter: boolean;
  metricKey?: string;
  getTrendColor: (trend: "UP" | "DOWN" | "STABLE", higherIsBetter: boolean) => string;
  getTrendIcon: (trend: "UP" | "DOWN" | "STABLE") => string;
  children: React.ReactElement;
}

const TrendTooltip: React.FC<TrendTooltipProps> = ({ 
  vsYesterday, 
  vsLastWeek, 
  higherIsBetter,
  metricKey,
  getTrendColor,
  getTrendIcon,
  children 
}) => {
  const { refs, floatingStyles } = useFloating({
    placement: "top",
    whileElementsMounted: autoUpdate,
    middleware: [offset(10), shift({ padding: 10 })],
  });
  const [isOpen, setIsOpen] = useState(false);

  if (!vsYesterday) {
    return <>{children}</>;
  }

  const isPercentageMetric = metricKey?.includes('perc') || metricKey?.includes('percentage');
  const formatBaseline = (value: number) => {
    return isPercentageMetric ? `${value.toFixed(1)}%` : value.toFixed(1);
  };

  return (
    <>
      <div
        ref={refs.setReference}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        style={{ display: "inline-block" }}
      >
        {children}
      </div>
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="trend-tooltip"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <div className="trend-tooltip__content">
            <div className="trend-tooltip__row">
              <span className="trend-tooltip__label">vs Yesterday:</span>
              <span 
                className="trend-tooltip__value"
                style={{ color: getTrendColor(vsYesterday.trend, higherIsBetter) }}
              >
                {getTrendIcon(vsYesterday.trend)} {Math.abs(vsYesterday.change_pct).toFixed(1)}%
              </span>
              <span className="trend-tooltip__baseline">
                (from {formatBaseline(vsYesterday.baseline)})
              </span>
            </div>
            {vsLastWeek && (
              <div className="trend-tooltip__row">
                <span className="trend-tooltip__label">vs Last Week:</span>
                <span 
                  className="trend-tooltip__value"
                  style={{ color: getTrendColor(vsLastWeek.trend, higherIsBetter) }}
                >
                  {getTrendIcon(vsLastWeek.trend)} {Math.abs(vsLastWeek.change_pct).toFixed(1)}%
                </span>
                <span className="trend-tooltip__baseline">
                  (from {formatBaseline(vsLastWeek.baseline)})
                </span>
              </div>
            )}
            <div className="trend-tooltip__footer">
              Click to view trend
            </div>
          </div>
          <div className="trend-tooltip__tail"></div>
        </div>
      )}
    </>
  );
};

export default TrendTooltip;

